import {
  CAPTION_WILDCARDS,
  type WildcardContext,
  type WildcardDef,
} from './captionWildcards.js';

// Publishing — WHAT THE "Insert wildcard" MENU PUTS IN THE CAPTION (2026-08-05).
//
// ⚠️⚠️ THE AUTHORING MODEL NOW DIFFERS BY COMPOSER, DELIBERATELY:
//
//   ComposerPage (/publishing/new, /:id/edit)  — ONE account   → 'resolved'
//     Inserts the RESOLVED TEXT at the caret. What you see is what posts, and
//     it stays editable in place. With exactly one target account there is
//     nothing to defer, so deferring only hides the value from the author.
//
//   MultiComposerPage (/publishing/multi)      — MANY accounts → 'token'
//     Inserts the `{token}`, resolved PER ACCOUNT at dispatch. This is the whole
//     point of the wildcard system — a shared caption is only correct across
//     accounts because the account-specific bits resolve per account — and it
//     must not regress.
//
// That is a divergence, not an inconsistency: one account = no deferral needed,
// many accounts = deferral IS the feature. The two composers are separate routes,
// so there is no mode-switching hazard on a single screen.
//
// Nothing about DISPATCH changes: the server still resolves any `{token}` a
// stored post carries (legacy captions, multi-account posts, anything typed by
// hand), so resolve-on-insert is purely an authoring convenience layered on top.

export type WildcardInsertMode = 'token' | 'resolved';

export type WildcardMenuItem = {
  def: WildcardDef;
  /** The exact text to insert at the caret; null when the item can't be used. */
  insertText: string | null;
  /**
   * Why it can't be used (null when it can). Shown IN PLACE of a click target —
   * inserting a silently empty string would leave the AM with nothing to see and
   * no idea why.
   */
  unavailableReason: string | null;
};

/**
 * The menu rows for one composer. In 'token' mode every row inserts its
 * `{token}` and nothing is ever unavailable — an empty field on ONE account says
 * nothing about the others, which is exactly why the token is deferred there.
 * In 'resolved' mode a row inserts the account's real text, and a blank source
 * is surfaced as unavailable-with-a-reason.
 */
export function buildWildcardMenu(
  mode: WildcardInsertMode,
  ctx: WildcardContext | null,
): WildcardMenuItem[] {
  return CAPTION_WILDCARDS.map((def) => {
    if (mode === 'token') {
      return { def, insertText: `{${def.token}}`, unavailableReason: null };
    }
    // 'resolved' with no context is a disabled MENU (see wildcardMenuState); this
    // branch only guards against a caller rendering rows anyway.
    const value = ctx ? def.resolve(ctx) : '';
    if (value.trim() === '') {
      return {
        def,
        insertText: null,
        unavailableReason: `No ${def.label.toLowerCase()} set for this account`,
      };
    }
    return { def, insertText: value, unavailableReason: null };
  });
}

export type WildcardMenuState = { disabled: boolean; reason: string | null };

/**
 * Whether the whole menu is usable. In 'resolved' mode there is nothing to insert
 * until the account's details have been resolved, so the menu is DISABLED with a
 * visible reason rather than silently falling back to inserting a token —
 * predictable beats clever, and mixing the two behaviours on one page is the
 * confusing outcome this split exists to avoid.
 */
export function wildcardMenuState(
  mode: WildcardInsertMode,
  ctx: WildcardContext | null,
  accountSelected: boolean,
): WildcardMenuState {
  if (mode === 'token') return { disabled: false, reason: null };
  if (!accountSelected) return { disabled: true, reason: 'Select an account first' };
  if (!ctx) return { disabled: true, reason: 'Loading account details…' };
  return { disabled: false, reason: null };
}

/**
 * Whether to warn that the caption may still hold the PREVIOUS account's details.
 *
 * Resolve-on-insert means an inserted phone number is literal text belonging to
 * whichever account was selected when it was inserted. Re-resolving it would mean
 * tracking which spans came from where — over-engineering, and wrong as soon as
 * the AM edits one. Instead the text stays VISIBLE and we say so once: a wrong
 * phone number in plain sight gets caught, which a silent mismatch does not.
 */
export function shouldWarnAccountChanged(
  previousAccountId: string,
  nextAccountId: string,
  caption: string,
): boolean {
  if (previousAccountId === '' || previousAccountId === nextAccountId) return false;
  return caption.trim() !== '';
}

export const ACCOUNT_CHANGED_WARNING =
  'Account changed — check any contact details in the caption are still correct.';
